import { useState } from 'react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { TagCategoryForm } from '../../../components/tags';
import { createTagCategory, ApiError } from '../../../lib/apiClient';
import {
  buildValidationFromForm,
  type TagCategoryFormData,
} from '../../../components/tags/TagCategoryForm';
import { useAppSelector } from '../../../store';
import { selectUser } from '../../../store/features/auth/authSlice';
import '@tylertech/forge/icon';
import '@tylertech/forge/banner';

export const Route = createFileRoute('/tags/categories/new')({
  component: CreateTagCategoryPage,
});

export { CreateTagCategoryPage };

function CreateTagCategoryPage(): JSX.Element {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currentUser = useAppSelector(selectUser);

  const IconElement = 'forge-icon' as React.ElementType;
  const BannerElement = 'forge-banner' as React.ElementType;

  const userTeams =
    currentUser?.primaryContactForTeams?.map((teamId) => ({
      id: teamId,
      name: teamId,
    })) ?? [];

  const handleSubmit = async (data: TagCategoryFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);
    try {
      await createTagCategory({
        name: data.name,
        description: data.description || undefined,
        scope: data.scope,
        scopeOwnerId: data.scope === 'team' ? data.scopeOwnerId : undefined,
        color: data.color || undefined,
        allowMultiple: data.allowMultiple,
        valueType: data.valueType,
        validation: buildValidationFromForm(data),
      });
      void navigate({ to: '/tags', search: { page: 1, pageSize: 25, categoryId: '' } });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    void navigate({ to: '/tags', search: { page: 1, pageSize: 25, categoryId: '' } });
  };

  return (
    <div className="create-tag-category-page">
      <div className="breadcrumb">
        <Link
          to="/tags"
          search={{ page: 1, pageSize: 25, categoryId: '' }}
          className="breadcrumb-link"
        >
          <IconElement name="arrow_back" aria-hidden="true" />
          <span>Back to Tags</span>
        </Link>
      </div>

      <div className="page-header">
        <h1 className="forge-typography--headline4">Create Tag Category</h1>
      </div>

      {error && (
        <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
          <div className="banner-content">
            <IconElement name="error" aria-hidden="true" />
            <span>{error}</span>
          </div>
        </BannerElement>
      )}

      <TagCategoryForm
        mode="create"
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
        currentUser={currentUser}
        userTeams={userTeams}
      />
    </div>
  );
}
